import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Edit3, Plus, UserX, Shield, Users, Layers, Trash2, ChefHat, LogOut, KeyRound, AlertTriangle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePOS } from "@/context/POSContext";
import * as admin from "@/lib/adminApi";
import { listStations } from "@/lib/kdsApi";
import { listItems as listStockItems } from "@/lib/inventoryApi";
import { listDepartments } from "@/lib/purchaseApi";
import { ConfirmDialog } from "@/components/admin/menu/shared";
import { listShifts } from "@/lib/attendanceApi";
import { t } from "@/lib/lang";

// Backend enum. There is no "manager" or "receptionist" — those names shipped in the
// Vibez design and have bitten this codebase three times now.
const ROLES = ["admin", "cashier", "captain"];

const blank = { employee_id: "", name: "", role: "captain", staff_role_id: null, pin: "", is_active: true };

// The per-person switches, grouped the way the floor thinks about them. Groups
// the catalogue adds later still show, after these, under their own name.
const GROUPS = [
  ["access", "Screens"],
  ["floor", "Table & order actions"],
  ["billing", "Billing"],
  ["orders", "Orders"],
  ["reports", "Reports"],
  ["inventory", "Inventory"],
  ["purchase", "Purchase"],
  ["register", "Register"],
  ["attendance", "Attendance"],
  ["crm", "CRM & khata"],
  ["pages", "Back-office pages"],
  ["masters", "Records — view, edit, import, export"],
  ["settings", "Settings"],
  ["printing", "Printing"],
  ["system", "System"],
  ["users", "Staff & permissions"],
];

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail) return "Request rejected";
  return failure?.message || "Could not reach the server";
};

/**
 * Edit one person: details, what they may do, and (captains) their section.
 *
 * Owner, 2026-10-18: the dialog offered Active and, for captains, Can settle —
 * nothing else. Every switch here is enforced by the server (bible §38); this
 * screen only edits the person's overrides on top of their role:
 *   Role default (yes/no) · Allow · Deny.
 * A captain given floors and/or tables works only those; none = every table.
 */
function StaffDialog({ member, onSave, onClose, onPinGenerated }) {
  const { token } = usePOS();
  const [form, setForm] = useState(
    member
      ? { ...member, staff_role_id: member.staff_role_id ?? null, shift_id: member.shift_id ?? null, pin: "" }
      : { ...blank, shift_id: null }
  );
  const [shifts, setShifts] = useState([]);
  useEffect(() => {
    if (!token) return;
    listShifts(token).then(setShifts).catch(() => setShifts([]));
  }, [token]);
  // Custom roles (2026-10-31). The picker lists them first, then the plain kinds.
  const [staffRoles, setStaffRoles] = useState([]);
  useEffect(() => {
    if (!token) return;
    admin.listStaffRoles(token).then(roles => {
      setStaffRoles(roles);
      // A new person starts on their kind's default role (Staff → Roles, owner 2026-11-01).
      if (!member) {
        setForm(f => {
          if (f.staff_role_id) return f;
          const chosen = roles.find(r => r.base_kind === f.role && r.is_default);
          return chosen ? { ...f, staff_role_id: chosen.id } : f;
        });
      }
    }).catch(() => setStaffRoles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const pickedRole = staffRoles.find(r => r.id === form.staff_role_id) || null;
  const pickRole = value => {
    if (value.startsWith("r:")) {
      const role = staffRoles.find(r => r.id === Number(value.slice(2)));
      if (role) setForm(f => ({ ...f, staff_role_id: role.id, role: role.base_kind }));
    } else {
      setForm(f => ({ ...f, staff_role_id: null, role: value.slice(2) }));
    }
  };
  const [activeTab, setActiveTab] = useState("profile");
  const [saving, setSaving] = useState(false);
  const editing = Boolean(member?.id);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  // `access.loaded` gates the overrides PUT: it is a full replace, so a failed
  // fetch must never be followed by a save that wipes overrides it never saw.
  const [access, setAccess] = useState({ loaded: false, error: null, catalog: [], overrides: {}, rolePerms: [] });
  const [tables, setTables] = useState({ loaded: false, error: null, rows: [] });
  const [floors, setFloors] = useState(member?.assigned_floors || []);
  const [tableIds, setTableIds] = useState(member?.assigned_table_ids || []);
  // Kitchen vs bar (2026-10-20): the KDS stations and the stock this person works.
  // Empty = no limit. Sent only once both lists loaded, so a failed fetch never
  // wipes what was set.
  const [scopeData, setScopeData] = useState({ loaded: false, error: null, stations: [], stock: [] });
  const [stationIds, setStationIds] = useState(member?.kds_station_ids || []);
  const [stockCats, setStockCats] = useState(member?.stock_categories || []);
  const [stockIds, setStockIds] = useState(member?.stock_item_ids || []);
  // Purchase departments they buy for (owner 2026-11-01: "saket manages kitchen purchase
  // and beverage purchase"). Loaded on its own: an editor without purchase access sees
  // why, and nothing is sent, so a failed read never clears what was set.
  const [depts, setDepts] = useState({ loaded: false, error: null, rows: [] });
  const [deptIds, setDeptIds] = useState(member?.purchase_department_ids || []);
  // Menu sections their item reports and KDS cover (owner 2026-11-01: "only give HS
  // and CIG perms … same for KDS"). Loaded on its own, like departments.
  const [menuCats, setMenuCats] = useState({ loaded: false, error: null, rows: [] });
  const [menuCatIds, setMenuCatIds] = useState(member?.menu_category_ids || []);
  const [stockSearch, setStockSearch] = useState("");
  const [openCats, setOpenCats] = useState([]);
  // Owner 2026-10-31: "shouldn't selecting a department auto select items?" A station
  // brings the stock its dishes' recipes use (no recipes yet = nothing to bring);
  // unpicking it takes back only what no other picked station also uses.
  const pickStation = (station, on) => {
    const others = (scopeData.stations || []).filter(s => s.id !== station.id && stationIds.includes(s.id));
    const keep = new Set(others.flatMap(s => s.stock_item_ids || []));
    setStationIds(ids => (on ? [...ids, station.id] : ids.filter(id => id !== station.id)));
    const theirs = station.stock_item_ids || [];
    setStockIds(ids => (on
      ? [...new Set([...ids, ...theirs])]
      : ids.filter(id => !theirs.includes(id) || keep.has(id))));
    if (on && theirs.length) toast.info(`${station.name}: ${theirs.length} stock item(s) its recipes use were picked too`);
  };

  useEffect(() => {
    if (!editing || !token) return undefined;
    let stale = false;
    Promise.all([admin.permissionCatalog(token), admin.userPermissions(member.id, token)])
      .then(([catalog, mine]) => {
        if (stale) return;
        setAccess({
          loaded: true, error: null, catalog,
          overrides: mine.overrides || {}, rolePerms: mine.role_permissions || [],
        });
      })
      .catch(failure => { if (!stale) setAccess(a => ({ ...a, error: errorText(failure) })); });
    admin.listTables(token)
      .then(rows => { if (!stale) setTables({ loaded: true, error: null, rows }); })
      .catch(failure => { if (!stale) setTables({ loaded: false, error: errorText(failure), rows: [] }); });
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/reports/filters/menu`,
              { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!stale) setMenuCats({ loaded: true, error: null, rows: data.categories || [] }); })
      .catch(failure => { if (!stale) setMenuCats({ loaded: false, error: errorText(failure), rows: [] }); });
    listDepartments(token)
      .then(rows => { if (!stale) setDepts({ loaded: true, error: null, rows }); })
      .catch(failure => { if (!stale) setDepts({ loaded: false, error: errorText(failure), rows: [] }); });
    Promise.all([listStations(), listStockItems({ activeOnly: false }, token)])
      .then(([stations, stock]) => { if (!stale) setScopeData({ loaded: true, error: null, stations, stock }); })
      .catch(failure => { if (!stale) setScopeData(d => ({ ...d, error: errorText(failure) })); });
    return () => { stale = true; };
  }, [editing, member, token]);

  // The role's own answer for a key. After a role change in this form the saved
  // role's set is stale, so fall back to the catalogue default for the new role.
  const byKey = useMemo(() => Object.fromEntries(access.catalog.map(p => [p.key, p])), [access.catalog]);
  const roleHas = entry => {
    if (form.role === member?.role && (form.staff_role_id ?? null) === (member?.staff_role_id ?? null)) {
      return access.rolePerms.includes(entry.key);
    }
    // Picked in this form, not saved yet: the kind's default, then the custom role's
    // adds and removes (the server works it out the same way once saved).
    if (pickedRole?.removes.includes(entry.key)) return false;
    if (pickedRole?.adds.includes(entry.key)) return true;
    return (entry.default_roles || []).includes(form.role);
  };
  // A report with no setting of its own follows its parent: "All reports", or for
  // the item-level reports "Item sales summary" (2026-11-01), which itself follows
  // "All reports". Each step takes the person's own setting if there is one.
  const inherited = entry => {
    const parent = byKey[entry.parent];
    if (!parent) return roleHas(entry);
    const own = access.overrides[parent.key];
    return own ? own === "allow" : inherited(parent);
  };
  const parentName = entry => (entry.parent === "reports.view"
    ? "All reports"
    : (byKey[entry.parent]?.label || "its group").replace(/^Report: /, ""));

  const setOverride = (key, value) =>
    setAccess(a => {
      const overrides = { ...a.overrides };
      if (value) overrides[key] = value;
      else delete overrides[key];
      return { ...a, overrides };
    });

  const grouped = useMemo(() => {
    const known = GROUPS.map(([key]) => key);
    const extra = [...new Set(access.catalog.map(p => p.group))].filter(g => !known.includes(g));
    return [...GROUPS, ...extra.map(g => [g, g])]
      .map(([key, label]) => [key, label, access.catalog.filter(p => p.group === key)])
      .filter(([, , rows]) => rows.length);
  }, [access.catalog]);

  const floorNames = useMemo(
    () => [...new Set(tables.rows.map(t => t.floor).filter(Boolean))].sort(),
    [tables.rows]
  );
  const toggleFloor = name => setFloors(f => (f.includes(name) ? f.filter(x => x !== name) : [...f, name]));
  const toggleTable = id => setTableIds(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));
  const overrideCount = Object.keys(access.overrides).length;
  const stockCategories = useMemo(
    () => [...new Set(scopeData.stock.map(i => i.category).filter(Boolean))].sort(),
    [scopeData.stock]
  );
  const toggleIn = setter => value => setter(list => (list.includes(value) ? list.filter(x => x !== value) : [...list, value]));
  const scopeCount = stationIds.length + stockCats.length + stockIds.length;
  // Picked earlier, not in today's lists (printer switched off, category renamed).
  const staleStations = stationIds.filter(id => !scopeData.stations.some(st => st.id === id));
  const staleCats = stockCats.filter(c => !stockCategories.includes(c));
  const staleItems = stockIds.filter(id => !scopeData.stock.some(i => i.id === id));
  const staleChip = "h-7 px-2 rounded-md text-meta font-medium border border-warning bg-warning/10 text-foreground";

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const empId = (form.employee_id || "").trim();
    if (!empId) return toast.error("Username / ID is required");
    if (!editing && form.pin && (form.pin.length < 4 || form.pin.length > 20)) {
      return toast.error("PIN / password must be 4–20 characters");
    }
    if (editing && form.pin && (form.pin.length < 4 || form.pin.length > 20)) {
      return toast.error("PIN / password must be 4–20 characters");
    }
    setSaving(true);
    const captain = form.role === "captain";
    // An empty PIN on edit means "leave it alone" — sending "" would set an empty one.
    // A section only means something for a captain; any other role clears it. It is
    // sent only once the tables loaded, so a failed fetch never wipes a section.
    const body = editing
      ? {
          employee_id: empId,
          name: form.name.trim(), role: form.role, is_active: form.is_active,
          shift_id: form.shift_id ? Number(form.shift_id) : null,
          // Sent only when changed: handing out a role needs "manage permissions".
          ...((form.staff_role_id ?? null) !== (member?.staff_role_id ?? null) ? { staff_role_id: form.staff_role_id ?? null } : {}),
          ...(form.pin ? { pin: form.pin } : {}),
          // A floor renamed or a table removed since it was assigned is dropped here,
          // or the server's "No such floor" 400 would make this person unsaveable.
          ...(tables.loaded
            ? {
                assigned_floors: captain ? floors.filter(f => floorNames.includes(f)) : [],
                assigned_table_ids: captain ? tableIds.filter(id => tables.rows.some(t => t.id === id)) : [],
              }
            : {}),
          // Admins are never limited, so their lists are cleared. A picked station or
          // stock that no longer shows is sent as it is, never dropped: dropping the
          // last one would quietly widen the person to everything. It is listed
          // below to clear by hand, and the server names it if it is truly gone.
          ...(scopeData.loaded
            ? {
                kds_station_ids: form.role === "admin" ? [] : stationIds,
                stock_categories: form.role === "admin" ? [] : stockCats,
                stock_item_ids: form.role === "admin" ? [] : stockIds,
              }
            : {}),
          ...(depts.loaded ? { purchase_department_ids: form.role === "admin" ? [] : deptIds } : {}),
          ...(menuCats.loaded ? { menu_category_ids: form.role === "admin" ? [] : menuCatIds } : {}),
        }
      : { employee_id: empId, name: form.name.trim(), role: form.role,
          ...(form.pin ? { pin: form.pin } : {}),
          ...(form.staff_role_id ? { staff_role_id: form.staff_role_id } : {}),
          ...(form.shift_id ? { shift_id: Number(form.shift_id) } : {}) };
    const result = await onSave(editing ? member.id : null, body);
    if (!result.ok) {
      setSaving(false);
      return toast.error(result.error);
    }
    if (editing && access.loaded) {
      try {
        await admin.setUserPermissions(member.id, access.overrides, token);
      } catch (failure) {
        setSaving(false);
        return toast.error(`Details saved, but the permissions were not: ${errorText(failure)}`);
      }
    }
    setSaving(false);
    if (!editing && result.data?.generated_pin && onPinGenerated) {
      onPinGenerated({
        name: form.name,
        employee_id: empId,
        pin: result.data.generated_pin,
      });
    } else {
      toast.success(editing ? `${form.name} updated` : `${form.name} added — open them again to set permissions and tables`);
    }
    onClose();
  };

  return (
    <Modal title={editing ? `Edit ${member.name}` : "Add staff"} onClose={onClose} testId="staff-dialog"
      footer={<>
        <Button variant="outline" onClick={onClose} data-testid="save-staff-button-cancel">Cancel</Button>
        <Button variant="action" onClick={submit} disabled={saving} data-testid="save-staff-button">
          {saving ? "Saving…" : (editing ? "Save changes" : "Add staff member")}
        </Button>
      </>}>
      <div data-density="compact" className="staff-editor space-y-4 pt-1">
        {/* Navigation Tabs for Editing Mode */}
        {editing && (
          <div className="flex border-b border-border gap-2 pb-px mb-2 overflow-x-auto" data-testid="staff-editor-tabs">
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`flex shrink-0 whitespace-nowrap items-center gap-1.5 pb-2 px-2 text-meta font-medium border-b-2 transition-all ${
                activeTab === "profile"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Profile
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("permissions")}
              className={`flex shrink-0 whitespace-nowrap items-center gap-1.5 pb-2 px-2 text-meta font-medium border-b-2 transition-all ${
                activeTab === "permissions"
                  ? "border-primary text-primary font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              Permissions
              {overrideCount > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full text-micro font-semibold bg-primary/10 text-primary border border-primary/20">
                  {overrideCount}
                </span>
              )}
            </button>
            {form.role !== "admin" && (
              <button
                type="button"
                onClick={() => setActiveTab("scope")}
                className={`flex shrink-0 whitespace-nowrap items-center gap-1.5 pb-2 px-2 text-meta font-medium border-b-2 transition-all ${
                  activeTab === "scope"
                    ? "border-primary text-primary font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                data-testid="staff-scope-tab"
              >
                <ChefHat className="w-3.5 h-3.5" />
                Stations & stock
                {scopeCount > 0 && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary" />}
              </button>
            )}
            {form.role === "captain" && (
              <button
                type="button"
                onClick={() => setActiveTab("section")}
                className={`flex shrink-0 whitespace-nowrap items-center gap-1.5 pb-2 px-2 text-meta font-medium border-b-2 transition-all ${
                  activeTab === "section"
                    ? "border-primary text-primary font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Table section
                {(floors.length > 0 || tableIds.length > 0) && (
                  <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Tab 1: Basic Profile Details */}
        <div className={editing && activeTab !== "profile" ? "hidden" : "space-y-3"}>
          <div className="bg-card border border-border rounded-lg p-3.5 space-y-3 shadow-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-meta font-medium text-foreground">
                Username / ID
                <Input
                  value={form.employee_id}
                  onChange={e => set("employee_id", e.target.value)}
                  placeholder="e.g. Phone number or C03"
                  className="h-8 text-body bg-background border-border rounded-md"
                  data-testid="staff-employee-id-input"
                />
              </label>
              <label className="flex flex-col gap-1 text-meta font-medium text-foreground">
                Name
                <Input
                  value={form.name}
                  onChange={e => set("name", e.target.value)}
                  placeholder="Full name"
                  className="h-8 text-body bg-background border-border rounded-md"
                  data-testid="staff-name-input"
                />
              </label>
              <label className="flex flex-col gap-1 text-meta font-medium text-foreground">
                Role
                <select
                  value={form.staff_role_id ? `r:${form.staff_role_id}` : `k:${form.role}`}
                  onChange={e => pickRole(e.target.value)}
                  className="h-8 rounded-md border border-border bg-background px-2.5 text-body font-medium focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all"
                  data-testid="staff-role-select"
                >
                  {staffRoles.length > 0 && (
                    <optgroup label="Roles">
                      {staffRoles.map(r => <option value={`r:${r.id}`} key={r.id}>{r.name}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="Plain kind">
                    {ROLES.map(role => (
                      <option value={`k:${role}`} key={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {pickedRole?.description && (
                  <span className="text-micro text-muted-foreground font-normal" data-testid="staff-role-description">
                    {pickedRole.description}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-1 text-meta font-medium text-foreground">
                {editing ? "New PIN / Password (blank to keep)" : "PIN / Password (optional)"}
                <Input
                  type="password"
                  maxLength={20}
                  value={form.pin}
                  onChange={e => set("pin", e.target.value)}
                  placeholder={editing ? "Leave blank to keep" : "Leave blank to auto-generate 4-digit PIN"}
                  className="h-8 text-body bg-background border-border rounded-md font-mono"
                  data-testid="staff-pin-input"
                />
              </label>
              <label className="flex flex-col gap-1 text-meta font-medium text-foreground">
                Assigned Shift
                <select
                  value={form.shift_id ?? ""}
                  onChange={e => set("shift_id", e.target.value ? Number(e.target.value) : null)}
                  className="h-8 rounded-md border border-border bg-background px-2.5 text-body font-medium focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all"
                  data-testid="staff-shift-select"
                >
                  <option value="">Default (Outlet default shift)</option>
                  {shifts.map(s => (
                    <option value={s.id} key={s.id}>
                      {s.name} ({s.start_time} - {s.end_time})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {editing && (
              <div className="pt-2.5 border-t border-border/60">
                <label className="flex items-center justify-between cursor-pointer p-1">
                  <div className="flex flex-col">
                    <span className="text-meta font-medium text-foreground">Active account</span>
                    <span className="text-micro text-muted-foreground">Inactive staff cannot sign into POS or take orders</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => set("is_active", e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                    data-testid="staff-active-checkbox"
                  />
                </label>
              </div>
            )}
          </div>

          {!editing && (
            <p className="p-3 rounded-lg bg-muted/40 border border-border text-meta text-muted-foreground">
              Permissions and table sections are configured after creating the account: click edit on the staff member once added.
            </p>
          )}
        </div>

        {/* Tab 2: Permissions Customization Section */}
        {editing && (
          <section
            className={activeTab !== "permissions" ? "hidden" : "space-y-3"}
            data-testid="staff-permissions"
          >
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 pb-1.5 border-b border-border/60">
              <h4 className="text-body font-semibold text-foreground">
                What {form.name.split(" ")[0] || "they"} can do
                {overrideCount ? (
                  <span className="ml-2 text-primary font-medium text-meta">
                    · {overrideCount} customized from role
                  </span>
                ) : null}
              </h4>
              <span className="text-micro text-muted-foreground">Override role defaults per person</span>
            </div>

            {access.error && (
              <p className="text-meta text-destructive font-medium p-2.5 rounded-md bg-destructive/10 border border-destructive/20" data-testid="staff-permissions-error">
                Could not load permissions: {access.error}. Permissions will not be changed.
              </p>
            )}

            {!access.loaded && !access.error && (
              <p className="text-meta text-muted-foreground py-2">Loading permissions…</p>
            )}

            {access.loaded && (
              <div className="space-y-2.5">
                {grouped.map(([group, label, rows]) => (
                  <fieldset
                    key={group}
                    className="border border-border/80 rounded-lg bg-muted/15 p-2.5"
                    data-testid={`staff-perm-group-${group}`}
                  >
                    <legend className="text-meta font-medium text-muted-foreground px-2 py-0.5 bg-background border border-border/60 rounded-md">
                      {label}
                    </legend>
                    <div className="divide-y divide-border/40 mt-1">
                      {rows.map(entry => {
                        const value = access.overrides[entry.key] || "";
                        const isOverridden = Boolean(value);
                        return (
                          <div
                            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1.5 px-2 rounded-md transition-colors ${
                              isOverridden
                                ? "bg-primary/8 font-medium text-foreground"
                                : "hover:bg-muted/30 text-muted-foreground"
                            }`}
                            key={entry.key}
                          >
                            <span className="text-meta font-medium text-foreground">{entry.label}</span>
                            <select
                              value={value}
                              onChange={e => setOverride(entry.key, e.target.value)}
                              aria-label={entry.label}
                              className={`h-7 px-2 rounded-md border text-meta font-medium outline-none transition-all ${
                                isOverridden
                                  ? "border-primary bg-background text-primary font-semibold"
                                  : "border-border bg-background text-foreground"
                              }`}
                              data-testid={`staff-perm-${entry.key}`}
                            >
                              <option value="">
                                {entry.parent
                                  ? `Same as ${parentName(entry)} (${inherited(entry) ? "yes" : "no"})`
                                  : `Role default (${roleHas(entry) ? "yes" : "no"})`}
                              </option>
                              <option value="allow">Allow</option>
                              <option value="deny">Deny</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Tab: KDS stations and stock (kitchen vs bar) */}
        {editing && form.role !== "admin" && (
          <section className={activeTab !== "scope" ? "hidden" : "space-y-3"} data-testid="staff-scope">
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 pb-1.5 border-b border-border/60">
              <h4 className="text-body font-semibold text-foreground">Stations & stock</h4>
              <span className="text-micro text-muted-foreground">Nothing picked = everything</span>
            </div>
            {scopeData.error && (
              <p className="text-meta text-destructive font-medium p-2.5 rounded-md bg-destructive/10 border border-destructive/20" data-testid="staff-scope-error">
                Could not load stations or stock: {scopeData.error}. They will not be changed.
              </p>
            )}
            {!scopeData.loaded && !scopeData.error && <p className="text-meta text-muted-foreground py-2">Loading…</p>}
            {scopeData.loaded && (
              <div className="space-y-2.5">
                <fieldset className="border border-border/80 rounded-lg bg-muted/15 p-2.5" data-testid="staff-scope-stations">
                  <legend className="text-meta font-medium text-muted-foreground px-2 py-0.5 bg-background border border-border/60 rounded-md">Kitchen display (KDS)</legend>
                  <p className="text-meta text-muted-foreground mb-1.5">
                    A station is a printer dishes' KOTs go to. Pick the ones this person cooks for — their KDS and
                    Telegram then show only those KOTs. Picking a station also picks the stock its dishes' recipes use.
                  </p>
                  <p className="text-micro text-muted-foreground mb-1.5" data-testid="staff-scope-stations-summary">
                    {stationIds.length ? "Sees and bumps only these stations' KOTs" : "Nothing picked: sees every station"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {scopeData.stations.map(st => {
                      const on = stationIds.includes(st.id);
                      return (
                        <button type="button" key={st.id} aria-pressed={on} onClick={() => pickStation(st, !on)}
                          className={`h-9 px-snug rounded-md text-meta font-medium border transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-muted"}`} data-testid={`staff-scope-station-${st.id}`}>
                          {st.name}{st.dishes ? <span className="opacity-75"> · {st.dishes} dishes</span> : null}
                        </button>
                      );
                    })}
                    {staleStations.map(id => (
                      <button type="button" key={`stale-${id}`} onClick={() => toggleIn(setStationIds)(id)} className={staleChip}
                        title="Picked earlier; this printer is off or gone. Tap to clear." data-testid={`staff-scope-stale-station-${id}`}>
                        Station #{id} (off or gone) ✕
                      </button>
                    ))}
                    {!scopeData.stations.length && !staleStations.length && <span className="text-meta text-muted-foreground">No active printers.</span>}
                  </div>
                </fieldset>
                <fieldset className="border border-border/80 rounded-lg bg-muted/15 p-2.5" data-testid="staff-scope-stock">
                  <legend className="text-meta font-medium text-muted-foreground px-2 py-0.5 bg-background border border-border/60 rounded-md">Stock</legend>
                  <p className="text-meta text-muted-foreground mb-1.5">
                    The stock this person sees and moves on Inventory. "Whole" picks a category and every item in it,
                    including items added to it later.
                  </p>
                  <p className="text-micro text-muted-foreground mb-1.5" data-testid="staff-scope-stock-summary">
                    {stockCats.length || stockIds.length
                      ? `Sees and moves only: ${[...stockCats, ...(stockIds.length ? [`${stockIds.length} single item(s)`] : [])].join(", ")}`
                      : "Sees all stock"}
                  </p>
                  {/* ~400 items on a real outlet: folded categories with counts and a
                      search, not a wall of chips (owner walkthrough, 2026-10-31). Picked
                      items always show, so what is chosen is never hidden. */}
                  <Input value={stockSearch} onChange={e => setStockSearch(e.target.value)} placeholder="Search stock items"
                    className="h-9 mb-1.5" data-testid="staff-scope-stock-search" />
                  {[...stockCategories, null].map(cat => {
                    const items = scopeData.stock.filter(i => (cat === null ? !i.category : i.category === cat));
                    if (!items.length) return null;
                    const whole = cat !== null && stockCats.includes(cat);
                    const term = stockSearch.trim().toLowerCase();
                    const matches = term ? items.filter(i => i.name.toLowerCase().includes(term)) : [];
                    if (term && !matches.length) return null;
                    const picked = items.filter(i => stockIds.includes(i.id)).length;
                    const key = cat ?? "__none";
                    const open = openCats.includes(key) || Boolean(term);
                    const shown = open ? (term ? matches : items) : items.filter(i => stockIds.includes(i.id));
                    return (
                      <div key={key} className="py-1 border-t first:border-t-0 border-border/60" data-testid={`staff-scope-category-${cat ?? "none"}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {cat !== null ? (
                            <label className="inline-flex items-center gap-2 cursor-pointer text-meta font-semibold flex-1 min-w-0">
                              <input type="checkbox" checked={whole} onChange={() => toggleIn(setStockCats)(cat)}
                                className="w-4 h-4 rounded border-border text-primary" data-testid={`staff-scope-category-checkbox-${cat}`} />
                              <span className="truncate">{cat}</span>
                            </label>
                          ) : (
                            <span className="text-meta font-semibold flex-1">No category</span>
                          )}
                          <span className="text-micro text-muted-foreground tabular-nums">
                            {whole ? `all ${items.length}` : `${picked} of ${items.length}`}
                          </span>
                          {!whole && !term && (
                            <button type="button" className="h-8 px-2 rounded-md text-micro font-medium border border-border hover:bg-muted"
                              onClick={() => setOpenCats(list => (list.includes(key) ? list.filter(x => x !== key) : [...list, key]))}
                              aria-expanded={open} data-testid={`staff-scope-open-${cat ?? "none"}`}>
                              {open ? "Hide items" : "Pick items"}
                            </button>
                          )}
                        </div>
                        {!whole && shown.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {shown.map(item => {
                              const on = stockIds.includes(item.id);
                              return (
                                <button type="button" key={item.id} aria-pressed={on} onClick={() => toggleIn(setStockIds)(item.id)}
                                  className={`h-8 px-2 rounded-md text-meta font-medium border transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-muted"}`} data-testid={`staff-scope-item-${item.id}`}>{item.name}</button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(staleCats.length > 0 || staleItems.length > 0) && (
                    <div className="py-1" data-testid="staff-scope-stale-stock">
                      <span className="text-meta font-semibold">Picked earlier, no longer found</span>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {staleCats.map(cat => (
                          <button type="button" key={`stale-cat-${cat}`} onClick={() => toggleIn(setStockCats)(cat)} className={staleChip}
                            data-testid={`staff-scope-stale-category-${cat}`}>{cat} ✕</button>
                        ))}
                        {staleItems.map(id => (
                          <button type="button" key={`stale-item-${id}`} onClick={() => toggleIn(setStockIds)(id)} className={staleChip}
                            data-testid={`staff-scope-stale-item-${id}`}>Item #{id} ✕</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!scopeData.stock.length && !staleCats.length && !staleItems.length && <span className="text-meta text-muted-foreground">No stock items yet.</span>}
                </fieldset>
              </div>
            )}
            <fieldset className="border border-border/80 rounded-lg bg-muted/15 p-2.5" data-testid="staff-scope-menu">
              <legend className="text-meta font-medium text-muted-foreground px-2 py-0.5 bg-background border border-border/60 rounded-md">Menu sections (item reports and KDS)</legend>
              {menuCats.error && (
                <p className="text-meta text-muted-foreground" data-testid="staff-scope-menu-error">
                  Menu sections not shown ({menuCats.error}) — they will not be changed.
                </p>
              )}
              {menuCats.loaded && (
                <>
                  <p className="text-micro text-muted-foreground mb-1.5" data-testid="staff-scope-menu-summary">
                    {menuCatIds.length
                      ? "Item reports and the KDS show only these sections"
                      : "Nothing picked: item reports and the KDS cover the whole menu"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {menuCats.rows.map(c => {
                      const on = menuCatIds.includes(c.id);
                      return (
                        <button type="button" key={c.id} aria-pressed={on}
                          onClick={() => setMenuCatIds(ids => (on ? ids.filter(x => x !== c.id) : [...ids, c.id]))}
                          className={`h-9 px-snug rounded-md text-meta font-medium border transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-muted"}`}
                          data-testid={`staff-scope-menu-${c.id}`}>
                          {c.name}{c.is_active ? "" : " (off)"}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </fieldset>
            <fieldset className="border border-border/80 rounded-lg bg-muted/15 p-2.5" data-testid="staff-scope-departments">
              <legend className="text-meta font-medium text-muted-foreground px-2 py-0.5 bg-background border border-border/60 rounded-md">Purchase departments</legend>
              {depts.error && (
                <p className="text-meta text-muted-foreground" data-testid="staff-scope-departments-error">
                  Departments not shown ({depts.error}) — they will not be changed.
                </p>
              )}
              {depts.loaded && (
                <>
                  <p className="text-micro text-muted-foreground mb-1.5" data-testid="staff-scope-departments-summary">
                    {deptIds.length ? "Sees and enters only these departments' purchases" : "Nothing picked: buys for every department"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {depts.rows.map(d => {
                      const on = deptIds.includes(d.id);
                      return (
                        <button type="button" key={d.id} aria-pressed={on}
                          onClick={() => setDeptIds(ids => (on ? ids.filter(x => x !== d.id) : [...ids, d.id]))}
                          className={`h-9 px-snug rounded-md text-meta font-medium border transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-muted"}`}
                          data-testid={`staff-scope-department-${d.id}`}>
                          {d.name}{d.is_active ? "" : " (off)"}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </fieldset>
          </section>
        )}

        {/* Tab 3: Captain Tables Section */}
        {editing && form.role === "captain" && (
          <section
            className={activeTab !== "section" ? "hidden" : "space-y-3"}
            data-testid="staff-section"
          >
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 pb-1.5 border-b border-border/60">
              <h4 className="text-body font-semibold text-foreground">Tables & Sections</h4>
              <span className="text-micro text-muted-foreground">Assigned section for this captain</span>
            </div>

            <p className="p-2 rounded-md bg-muted/40 border border-border text-meta text-muted-foreground" data-testid="staff-section-summary">
              {floors.length || tableIds.length
                ? `Works only: ${[...floors, ...(tableIds.length ? [`${tableIds.length} single table(s)`] : [])].join(", ")}`
                : "No section — works every table"}
            </p>

            {tables.error && (
              <p className="text-meta text-destructive font-medium p-2.5 rounded-md bg-destructive/10 border border-destructive/20" data-testid="staff-tables-error">
                Could not load tables: {tables.error}. The section will not be changed.
              </p>
            )}

            {tables.loaded && (
              <div className="space-y-2.5">
                {floorNames.map(name => {
                  const whole = floors.includes(name);
                  return (
                    <fieldset
                      key={name}
                      className="border border-border/80 rounded-lg p-2.5 bg-muted/15 space-y-2"
                      data-testid={`staff-floor-${name}`}
                    >
                      <legend className="text-meta font-medium text-foreground px-1">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={whole}
                            onChange={() => toggleFloor(name)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            data-testid={`staff-floor-checkbox-${name}`}
                          />
                          <span className="text-meta font-semibold">Whole {name}</span>
                        </label>
                      </legend>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {tables.rows.filter(t => t.floor === name).map(t => {
                          const on = whole || tableIds.includes(t.id);
                          return (
                            <button
                              type="button"
                              key={t.id}
                              className={`h-7 min-w-[36px] px-2 rounded-md font-mono text-meta font-medium transition-all border ${
                                on
                                  ? "bg-primary text-primary-foreground border-primary shadow-xs"
                                  : "bg-background text-foreground border-border hover:bg-muted"
                              } ${whole ? "opacity-75 cursor-not-allowed" : ""}`}
                              disabled={whole}
                              aria-pressed={on}
                              onClick={() => toggleTable(t.id)}
                              data-testid={`staff-table-${t.id}`}
                            >
                              {t.number}
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>
                  );
                })}
              </div>
            )}
          </section>
        )}

      </div>
    </Modal>
  );
}

function PinDisplayModal({ notice, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!notice) return null;

  const handleCopy = () => {
    if (notice.pin) {
      navigator.clipboard?.writeText(notice.pin);
      setCopied(true);
      toast.success("PIN copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      title="Staff PIN Generated"
      onClose={onClose}
      testId="pin-display-modal"
      footer={
        <Button variant="action" onClick={onClose} data-testid="pin-display-done-button">
          Done
        </Button>
      }
    >
      <div className="space-y-4 py-2">
        <div className="p-3 bg-muted/40 rounded-lg border border-border">
          <p className="text-body font-semibold text-foreground">{notice.name}</p>
          <p className="text-meta text-muted-foreground font-mono">{notice.employee_id}</p>
        </div>

        <div className="text-center space-y-2">
          <label className="text-meta font-medium text-muted-foreground block">
            Generated 4-Digit PIN
          </label>
          <div className="flex items-center justify-center gap-2">
            <div
              className="px-6 py-3 bg-card border-2 border-primary/40 rounded-lg text-3xl font-mono tracking-[0.3em] font-bold text-foreground select-all shadow-inner"
              data-testid="generated-pin-display"
            >
              {notice.pin}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopy}
              className="h-12 w-12 shrink-0"
              title="Copy PIN"
            >
              {copied ? <Check className="w-5 h-5 text-success" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg text-meta text-warning-foreground flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
          <span>
            Make note of this PIN now. For security, PINs are hashed on the server and cannot be viewed again.
          </span>
        </div>
      </div>
    </Modal>
  );
}

function PinResetModal({ member, token, onClose, onPinGenerated, onRefresh }) {
  const [customPin, setCustomPin] = useState("");
  const [busy, setBusy] = useState(false);
  if (!member) return null;

  const handleRegenerate = async () => {
    setBusy(true);
    try {
      const res = await admin.regeneratePin(member.id, token);
      toast.success(`New PIN generated for ${member.name}`);
      onClose();
      onPinGenerated({
        name: member.name,
        employee_id: member.employee_id,
        pin: res.pin,
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to regenerate PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleSetCustom = async () => {
    if (!customPin || customPin.length < 4 || customPin.length > 20) {
      return toast.error("PIN must be 4–20 characters");
    }
    setBusy(true);
    try {
      await admin.setCustomPin(member.id, customPin, token);
      toast.success(`PIN updated for ${member.name}`);
      onClose();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleUnlock = async () => {
    setBusy(true);
    try {
      await admin.unlockPin(member.id, token);
      toast.success(`Account unlocked for ${member.name}`);
      onClose();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unlock account");
    } finally {
      setBusy(false);
    }
  };

  const isLocked = Boolean(member.pin_locked_until && new Date(member.pin_locked_until) > new Date());

  return (
    <Modal
      title={`PIN Actions: ${member.name}`}
      onClose={onClose}
      testId="pin-reset-modal"
      footer={
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-4 py-2">
        {isLocked && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center justify-between gap-2">
            <div>
              <p className="text-meta font-semibold text-destructive">Account Locked</p>
              <p className="text-micro text-muted-foreground">
                Locked due to excessive failed PIN attempts.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnlock}
              disabled={busy}
              className="shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10"
              data-testid="unlock-account-button"
            >
              Unlock Now
            </Button>
          </div>
        )}

        <div className="p-3 bg-card border border-border rounded-lg space-y-2">
          <h4 className="text-meta font-semibold text-foreground">Auto-Generate New PIN</h4>
          <p className="text-micro text-muted-foreground">
            Generate a fresh random 4-digit PIN. This will immediately replace the previous PIN.
          </p>
          <Button
            variant="action"
            onClick={handleRegenerate}
            disabled={busy}
            className="w-full"
            data-testid="regenerate-pin-button"
          >
            <KeyRound className="w-3.5 h-3.5 mr-1.5" />
            Regenerate 4-Digit PIN
          </Button>
        </div>

        <div className="p-3 bg-card border border-border rounded-lg space-y-2">
          <h4 className="text-meta font-semibold text-foreground">Or Set Custom PIN</h4>
          <div className="flex items-center gap-2">
            <Input
              type="password"
              maxLength={20}
              value={customPin}
              onChange={e => setCustomPin(e.target.value)}
              placeholder="Enter new 4-digit PIN"
              className="h-9 font-mono"
              data-testid="custom-pin-input"
            />
            <Button
              variant="outline"
              onClick={handleSetCustom}
              disabled={busy || !customPin}
              className="shrink-0"
              data-testid="set-custom-pin-button"
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Staff accounts, from /api/users.
 *
 * The panel renders active and inactive users with clean badges, 40px table rows,
 * and standard actions aligned precisely to header columns.
 */
export default function StaffPanel({ staff = [], onSave, onDeactivate, onRemove, onRefresh }) {
  const { token } = usePOS();
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [confirmSignOutAll, setConfirmSignOutAll] = useState(false);
  const [pinNotice, setPinNotice] = useState(null);
  const [pinMember, setPinMember] = useState(null);

  const handleSignOutEveryone = async () => {
    setSigningOutAll(true);
    try {
      await admin.signOutEveryone(token);
      toast.success(t("सभी को साइन आउट कर दिया गया है · Everyone signed out"));
      setConfirmSignOutAll(false);
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign everyone out");
      setSigningOutAll(false);
    }
  };
  const [editing, setEditing] = useState(null);
  // The list names each person's role (Kitchen, Storekeeper…), not only their kind.
  const [roleNames, setRoleNames] = useState({});
  useEffect(() => {
    if (!token) return;
    admin.listStaffRoles(token)
      .then(rows => setRoleNames(Object.fromEntries(rows.map(r => [r.id, r.name]))))
      .catch(() => setRoleNames({}));
  }, [token, staff]);
  const [removing, setRemoving] = useState(null);

  // A staff row is never deleted: bills and the audit log name it. "Remove" takes an
  // inactive account off this list for good and frees its ID (2026-10-20).
  const remove = async member => {
    setRemoving(null);
    const result = await onRemove(member.id);
    if (result.ok) toast.success(`${member.name} removed`);
    else toast.error(`${member.name} was not removed: ${result.error}`);
  };

  const deactivate = async member => {
    const result = await onDeactivate(member.id);
    if (result.ok) toast.success(`${member.name} deactivated`);
    else toast.error(result.error);
  };

  return (
    <section data-density="compact" data-testid="staff-accounts-panel">
      {/* Table Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-snug p-base border-b bg-card">
        <div>
          <h3 className="text-lede font-semibold text-foreground">Staff accounts</h3>
          <p className="text-meta text-muted-foreground">
            {staff.filter(s => s.is_active).length} active · {staff.length} total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setConfirmSignOutAll(true)}
            data-testid="sign-out-everyone-button"
            className="h-8 px-base font-medium shadow-xs text-danger hover:text-danger hover:bg-danger/10 border-danger/30"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" />
            {t("सभी को साइन आउट करें · Sign everyone out")}
          </Button>
          <Button variant="action"
            onClick={() => setEditing(blank)}
            data-testid="add-staff-account-button"
            className="h-8 px-base font-medium shadow-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add staff
          </Button>
        </div>
      </div>

      {/* Table Header and Rows */}
      <div className="overflow-x-auto scroll-contain-x">
        <div className="min-w-[560px]">
          {/* Header Row */}
          <div className="grid grid-cols-[40px_1fr_120px_100px_110px] items-center gap-snug px-base py-2.5 bg-muted/40 border-b text-meta font-medium text-muted-foreground">
            <span className="sr-only">Avatar</span>
            <span>Staff member</span>
            <span>Role</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Data Rows */}
          {staff.map((member, index) => {
            const initials = member.name
              ? member.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
              : "??";
            const roleBadgeClass =
              member.role === "admin"
                ? "bg-primary/10 text-primary border-primary/20"
                : member.role === "cashier"
                ? "bg-info/10 text-info border-info/20"
                : "bg-warning/10 text-warning border-warning/20";

            return (
              <div
                className={`staff-row grid grid-cols-[40px_1fr_120px_100px_110px] items-center gap-snug px-base py-2 hover:bg-muted/20 transition-colors ${
                  member.is_active ? "" : "inactive opacity-60"
                }`}
                key={member.id}
                data-testid={`staff-member-${index}`}
              >
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-semibold text-meta shrink-0">
                  {initials}
                </span>

                <div className="flex flex-col min-w-0 pr-2">
                  <div className="flex items-center gap-1.5">
                    <strong className="text-body font-medium text-foreground truncate">{member.name}</strong>
                    {member.attendance_overrides_review && (
                      <span
                        title="Policy review needed (shift or role changed)"
                        className="inline-flex items-center text-warning shrink-0"
                        data-testid={`policy-review-badge-${index}`}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  <small className="text-micro font-mono text-muted-foreground">{member.employee_id}</small>
                </div>

                <div>
                  <b className={`inline-flex items-center px-2 py-0.5 rounded-full text-micro font-medium border ${roleBadgeClass}`}>
                    {roleNames[member.staff_role_id] || member.role}
                  </b>
                </div>

                <div>
                  <span className="inline-flex items-center gap-1.5 text-meta">
                    <span className={`w-2 h-2 rounded-full ${member.is_active ? "bg-success" : "bg-muted-foreground/40"}`} />
                    <i className="not-italic text-meta text-muted-foreground font-normal">{member.is_active ? "active" : "inactive"}</i>
                  </span>
                </div>

                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={() => setPinMember(member)}
                    data-testid={`pin-actions-staff-member-${index}-button`}
                    aria-label={`PIN actions for ${member.name}`}
                    title={`PIN actions for ${member.name}`}
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={() => setEditing(member)}
                    data-testid={`edit-staff-member-${index}-button`}
                    aria-label={`Edit ${member.name}`}
                    title={`Edit ${member.name}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  {member.is_active && (
                    <button
                      type="button"
                      className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={() => deactivate(member)}
                      data-testid={`deactivate-staff-member-${index}-button`}
                      aria-label={`Deactivate ${member.name}`}
                      title={`Deactivate ${member.name} (an inactive account can then be removed)`}
                    >
                      <UserX className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {!member.is_active && onRemove && (
                    <button
                      type="button"
                      className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      onClick={() => setRemoving(member)}
                      data-testid={`remove-staff-member-${index}-button`}
                      aria-label={`Remove ${member.name}`}
                      title={`Remove ${member.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!staff.length && <p className="chart-empty py-base text-center text-meta text-muted-foreground" data-testid="staff-empty">No staff accounts.</p>}
      {editing && (
        <StaffDialog
          member={editing.id ? editing : null}
          onSave={onSave}
          onClose={() => setEditing(null)}
          onPinGenerated={setPinNotice}
        />
      )}
      <PinDisplayModal notice={pinNotice} onClose={() => setPinNotice(null)} />
      <PinResetModal
        member={pinMember}
        token={token}
        onClose={() => setPinMember(null)}
        onPinGenerated={setPinNotice}
        onRefresh={onRefresh}
      />
      <ConfirmDialog
        open={Boolean(removing)}
        title={removing ? `Remove ${removing.name}?` : ""}
        body={removing ? `${removing.name} leaves this list and can never sign in again, and the ID ${removing.employee_id} becomes free for someone new. Their name stays on every old bill, KOT and audit entry. This cannot be undone. To use them again, add them as a new account.` : ""}
        confirmLabel="Remove account"
        onCancel={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        testId="staff-remove-dialog"
      />
      <ConfirmDialog
        open={confirmSignOutAll}
        title={t("सभी को साइन आउट करें · Sign everyone out")}
        body={t(
          "क्या आप सभी डिवाइस से सभी कर्मचारियों को साइन आउट करना चाहते हैं? हर व्यक्ति के अगले साइन-इन पर ड्यूटी नए सिरे से शुरू होगी। · Are you sure you want to sign everyone out across all devices? Duty will start normally at each person's next sign-in."
        )}
        confirmLabel={t("हाँ, सभी को साइन आउट करें · Sign out everyone")}
        onCancel={() => setConfirmSignOutAll(false)}
        onConfirm={handleSignOutEveryone}
        testId="confirm-sign-out-everyone-modal"
      />
    </section>
  );
}
